import React, { useState, createContext, useContext } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ThemeProvider, CssBaseline, Box } from "@mui/material";
import { theme } from "./theme";
import Header from "./components/Header";
import ScrollToTop from "./components/ScrollToTop";
import Blogs from "./pages/Blogs";
import HackerNewsPage from "./pages/HackerNewsPage";
import HNStoryDetail from "./pages/HNStoryDetail";
import FullStackGuide from "./pages/FullStackGuide";
import YouTube from "./pages/YouTube";
import About from "./pages/About";
import BlogDetail from "./pages/BlogDetail";
import Support from "./pages/Support";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import RoutinexLegal from "./pages/RoutinexLegal";
import PujavidhiLegal from "./pages/PujavidhiLegal";
import PuzzleLegal from "./pages/PuzzleLegal";
import Contact from "./pages/Contact";
import QRGenerator from "./pages/QRGenerator";
import UnitConverter from "./pages/UnitConverter";
import PasswordGenerator from "./pages/PasswordGenerator";
import WordCounter from "./pages/WordCounter";
import ImageCompressionTool from "./pages/ImageCompressionTool";
import DeveloperFormatterToolkit from "./pages/DeveloperFormatterToolkit";
import ScannerLegal from "./pages/ScannerLegal";
import World from "./pages/World";

interface SearchBarContextType {
  setSearchBar: (searchBar: React.ReactNode) => void;
}

const SearchBarContext = createContext<SearchBarContextType | undefined>(
  undefined,
);

export const useSearchBar = () => {
  const context = useContext(SearchBarContext);
  if (!context) {
    throw new Error("useSearchBar must be used within SearchBarProvider");
  }
  return context;
};

function AppContent() {
  const [searchBar, setSearchBar] = useState<React.ReactNode>(null);

  return (
    <SearchBarContext.Provider value={{ setSearchBar }}>
      <Box
        sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}
      >
        <Header searchBar={searchBar} />
        <Box
          component="main"
          sx={{ flex: 1, display: "flex", flexDirection: "column" }}
        >
          <Routes>
            <Route path="/blogs" element={<Blogs />} />
            <Route path="/news" element={<HackerNewsPage />} />
            <Route path="/news/:id" element={<HNStoryDetail />} />
            <Route path="/tech-stacks" element={<FullStackGuide />} />
            <Route path="/youtube" element={<YouTube />} />
            <Route path="/about" element={<About />} />
            <Route path="/blog/:slug" element={<BlogDetail />} />
            <Route path="/support" element={<Support />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/qr-generator" element={<QRGenerator />} />
            <Route path="/unit-converter" element={<UnitConverter />} />
            <Route path="/password-generator" element={<PasswordGenerator />} />
            <Route path="/word-counter" element={<WordCounter />} />
            <Route
              path="/image-compression-tool"
              element={<ImageCompressionTool />}
            />
            <Route
              path="/developer-formatter-toolkit"
              element={<DeveloperFormatterToolkit />}
            />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route
              path="/terms-and-conditions"
              element={<TermsAndConditions />}
            />
            <Route path="/routinex/legal" element={<RoutinexLegal />} />
            <Route path="/pujavidhi/legal" element={<PujavidhiLegal />} />
            <Route path="/puzzle/legal" element={<PuzzleLegal />} />
            <Route path="/iamscanner/legal" element={<ScannerLegal />} />
            <Route path="*" element={<Blogs />} />
          </Routes>
        </Box>
      </Box>
    </SearchBarContext.Provider>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <ScrollToTop />
        <Routes>
          {/* Full-screen immersive 3D scene — no header/footer chrome. This is the homepage. */}
          <Route path="/" element={<World />} />
          <Route path="/world" element={<World />} />
          <Route path="/*" element={<AppContent />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
